import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import {
  ArtistPortalArtistRef,
  ArtistPortalAuthKind,
  ArtistPortalConnectFailure,
} from 'fym-shared/src/smartlinks/artistPortals';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { ServiceConnection, ServiceConnectionRequestReason } from '../../../redux/api/fymAPI.types';
import { ErrorAPIResult } from '../../../redux/api/utils/isSuccessfulResult';
import { catalogServiceCardCopy } from './catalogServiceCards';
import { ConnectChoice } from './ConnectServiceChoices';
import { ConnectServiceModalBody, ConnectStep } from './ConnectServiceModalBody';
import { ArtistPortalAuthUnsupportedError } from './artistPortalAuth/ArtistPortalAuthUnsupportedError';
import { ArtistPortalConnectResult, useArtistPortalConnect } from './useArtistPortalConnect';
import { useCallbackArtistPicker } from './useCallbackArtistPicker';
import { noArtistFoundMessage, portalConnectFailureMessages } from './portalConnectMessages';
import { ConnectOutcome, connectOutcome } from './connectOutcome';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';
import { nativeCatalogConnectDeepLink, openNativeLink, signInNeedsNativeConnect } from './nativeConnect';
import { isReturnPagePortal } from './portalReturn/returnPagePortals';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectServiceModalProps {
  accountId: number;
  connection?: ServiceConnection;
  closeModal: () => void;
  /** Files the request; a reason also sends it to support@music24.com. */
  onRequest: (integrationType: string, reason?: ServiceConnectionRequestReason) => Promise<boolean>;
  onUseProfileUrl: (integrationType: string) => void;
  isLoading: boolean;
  error?: ErrorAPIResult['error'];
  /**
   * The failure a YouTube sign-in came back with, when the sheet opens for it.
   * The Catalog screen reads it off the address (`usePortalConnectReturn`).
   */
  callbackFailure?: ArtistPortalConnectFailure;
  /** Opens a link of the app's own scheme. Tests pass their own function. */
  navigateNative?: (url: string) => void;
}

/**
 * The connect sheet.
 *
 * Signing in is real for the services we hold a provider for; the other
 * services keep the ladder of honest answers, because an option that does
 * nothing would be worse than one that says so.
 */
const ConnectServiceModalComponent: React.FC<ConnectServiceModalProps> = ({
  accountId,
  connection,
  closeModal,
  onRequest,
  onUseProfileUrl,
  isLoading,
  error,
  callbackFailure,
  navigateNative = openNativeLink,
}) => {
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const [choice, setChoice] = useState<ConnectChoice>();
  const [step, setStep] = useState<ConnectStep>('choices');
  const [artists, setArtists] = useState<ArtistPortalArtistRef[]>([]);
  const [isNoArtistFound, setNoArtistFound] = useState(false);
  const portal = useArtistPortalConnect({ accountId });
  const { clearError: clearPortalError } = portal;

  // The sheet stays mounted between cards, so every open starts clean: the
  // last card's error must not show on the next one.
  useEffect(() => {
    setChoice(undefined);
    setStep('choices');
    setArtists([]);
    setNoArtistFound(false);
    clearPortalError();
  }, [clearPortalError, connection]);

  const showPicker = useCallback((loaded: ArtistPortalArtistRef[]) => {
    setArtists(loaded);
    setStep('picker');
  }, []);
  useCallbackArtistPicker({ connection, loadArtists: portal.loadArtists, onLoaded: showPicker });

  const integrationType = connection?.integrationType;
  const copy = integrationType === undefined ? undefined : catalogServiceCardCopy[integrationType];
  const serviceName =
    copy !== undefined
      ? intl.formatMessage(copy.programName)
      : ((integrationType !== undefined ? musicServicesByMusicAPIID[integrationType]?.name : undefined) ?? '');

  const callbackFailureMessage =
    callbackFailure === undefined
      ? undefined
      : intl.formatMessage(portalConnectFailureMessages[callbackFailure], { service: serviceName });
  const noArtistMessage = isNoArtistFound
    ? intl.formatMessage(noArtistFoundMessage, { service: serviceName })
    : undefined;

  const canSignIn = connection?.connectAvailable === true && connection.authKind !== null;

  /**
   * One artist means the server already picked; several means the artist
   * must. None means the sign-in worked but holds no artist: say so, rather
   * than close on a connection that shows nothing.
   */
  const afterConnect = useCallback(
    (result: ArtistPortalConnectResult | null) => {
      if (result === null) {
        return;
      }
      const outcome = connectOutcome(result);
      if (outcome === ConnectOutcome.noArtist) {
        setNoArtistFound(true);
        setStep('choices');
        return;
      }
      if (outcome === ConnectOutcome.pickArtist) {
        setArtists(result.artists);
        setStep('picker');
        return;
      }
      closeModal();
    },
    [closeModal]
  );

  const onContinue = useCallback(() => {
    if (integrationType === undefined || choice === undefined) {
      return;
    }
    setNoArtistFound(false);
    if (choice === ConnectChoice.cannotConnect) {
      onUseProfileUrl(integrationType);
      return;
    }
    if (choice === ConnectChoice.signIn) {
      if (connection?.authKind === ArtistPortalAuthKind.credentials) {
        setStep('credentials');
        return;
      }
      // Inside the app's web view, Spotify for Artists and YouTube cannot sign in on this page:
      // the app reads the Spotify redirect itself, and Google refuses an embedded web view. The
      // app opens its own connect of the service instead.
      if (isNativeEmbed && signInNeedsNativeConnect(connection?.authKind)) {
        closeModal();
        navigateNative(nativeCatalogConnectDeepLink(integrationType));
        return;
      }
      if (connection?.authKind === ArtistPortalAuthKind.oauthCallback) {
        portal.connectWithCallback(integrationType).catch(console.error);
        return;
      }
      // Audiomack: MusicAPI runs the sign-in and sends the browser to the return page.
      if (isReturnPagePortal(integrationType)) {
        portal.connectWithReturnPage(integrationType).catch(console.error);
        return;
      }
      portal.connectSpotify(integrationType).then(afterConnect).catch(console.error);
      return;
    }
    // Both remaining choices send the request to support, who answer by email
    // (design product-gaps c1, c2). The sheet stays open to say so.
    const reason: ServiceConnectionRequestReason =
      choice === ConnectChoice.invitePending ? 'invitePending' : 'labelHoldsAccess';
    onRequest(integrationType, reason)
      .then((requested) => {
        if (!requested) {
          return;
        }
        setStep('requested');
      })
      .catch(console.error);
  }, [
    afterConnect,
    choice,
    closeModal,
    connection?.authKind,
    integrationType,
    isNativeEmbed,
    navigateNative,
    onRequest,
    onUseProfileUrl,
    portal,
  ]);

  const onCredentials = useCallback(
    (email: string, password: string) => {
      if (integrationType === undefined) {
        return;
      }
      portal.connectDeezer(integrationType, email, password).then(afterConnect).catch(console.error);
    },
    [afterConnect, integrationType, portal]
  );

  const onPickArtist = useCallback(
    (externalArtistId: string) => {
      if (integrationType === undefined) {
        return;
      }
      portal
        .selectArtist(integrationType, externalArtistId)
        .then((picked) => {
          if (!picked) {
            return;
          }
          closeModal();
        })
        .catch(console.error);
    },
    [closeModal, integrationType, portal]
  );

  const isBusy = isLoading || portal.isBusy;
  const isRequestChoice = choice === ConnectChoice.labelHoldsAccess || choice === ConnectChoice.invitePending;
  const isRequested = step === 'requested';
  const isUnsupported = portal.error instanceof ArtistPortalAuthUnsupportedError;

  return (
    <Modal isOpen={connection !== undefined} toggle={closeModal} centered size="lg" className="sml-modal">
      <ModalHeader toggle={closeModal}>
        {isUnsupported
          ? intl.formatMessage(
              { id: 'app.sml.catalog.connect.titleOnPhone', defaultMessage: 'Connect {service} on your phone' },
              { service: serviceName }
            )
          : intl.formatMessage(
              { id: 'app.sml.catalog.connect.title', defaultMessage: 'Connect {service}' },
              { service: serviceName }
            )}
      </ModalHeader>
      <ModalBody>
        <ConnectServiceModalBody
          accountId={accountId}
          integrationType={integrationType}
          onClose={closeModal}
          step={step}
          serviceName={serviceName}
          canSignIn={canSignIn}
          isBusy={isBusy}
          isUnsupported={isUnsupported}
          choice={choice}
          onChoose={setChoice}
          artists={artists}
          onCredentials={onCredentials}
          onPickArtist={onPickArtist}
          error={error}
          portalError={portal.error}
          callbackFailureMessage={callbackFailureMessage ?? noArtistMessage}
        />
      </ModalBody>
      <ModalFooter>
        {isRequested ? (
          <SMLButton color="primary" onClick={closeModal}>
            {intl.formatMessage({ id: 'app.sml.catalog.connect.done', defaultMessage: 'Done' })}
          </SMLButton>
        ) : (
          <>
            <p className={styles.sheetNote}>
              {intl.formatMessage({
                id: 'app.sml.catalog.connect.note',
                defaultMessage: 'Nothing in Promote is locked while you are unconnected.',
              })}
            </p>
            <SMLButton outline onClick={closeModal} disabled={isBusy}>
              {intl.formatMessage({ id: 'app.forms.buttons.cancel.label', defaultMessage: 'Cancel' })}
            </SMLButton>
          </>
        )}
        {step === 'choices' && !isUnsupported && (
          <SMLButton color="primary" onClick={onContinue} disabled={isBusy || choice === undefined}>
            {isRequestChoice
              ? intl.formatMessage({ id: 'app.sml.catalog.connect.sendRequest', defaultMessage: 'Send request' })
              : intl.formatMessage({ id: 'app.sml.catalog.connect.continue', defaultMessage: 'Continue' })}
          </SMLButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

export const ConnectServiceModal = TypedMemo(ConnectServiceModalComponent);
